/**
 * Central API Client for customer-admin-portal.
 *
 * DO NOT EDIT THIS FILE DIRECTLY!
 * TODO: https://linear.app/cargovibe-tech/issue/CAR-512/consolidate-customer-admin-portal-and-internal-admin-platform-http
 * http.consistency.test.ts in internal-admin-platform depends on this file until they both get consolidated
 */
import { z } from 'zod';

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api/cap';
const DEFAULT_TIMEOUT: number = 30_000;

type TokenGetter = () => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;

export function setAuthTokenGetter(getter: TokenGetter | null): void {
  tokenGetter = getter;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeout?: number;
}

class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown
  ) {
    super(extractBackendMessage(data) ?? `HTTP ${String(status)}: ${statusText}`);
    this.name = 'HttpError';
  }
}

function extractBackendMessage(data: unknown): string | null {
  if (data && typeof data === 'object' && 'error' in data) {
    const value: unknown = data.error;
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

async function request<T>(
  endpoint: string,
  schema: z.ZodType<T> | undefined,
  options: RequestOptions = {}
): Promise<T> {
  const { body, timeout = DEFAULT_TIMEOUT, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(customHeaders as Record<string, string>),
  };

  if (tokenGetter) {
    try {
      const token: string | null = await tokenGetter();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('[http] Failed to fetch Clerk token:', e);
    }
  }

  const controller: AbortController = new AbortController();
  const timeoutId: number = setTimeout((): void => {
    controller.abort();
  }, timeout);

  try {
    const response: Response = await fetch(`${BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('[API] Unauthorized – redirect to login');
      }

      if (response.status === 403) {
        console.warn('[API] Forbidden – insufficient permissions');
      }

      const errorData: unknown = await response.json().catch(() => null);
      throw new HttpError(response.status, response.statusText, errorData);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const json: unknown = await response.json();
    if (schema) return schema.parse(json);
    // Last-resort cast to the caller-specified `T`: only reached by callers not yet
    // migrated to a shared Zod schema (see routingApi.ts) — follow-up ticket pending.
    return json as T;
  } finally {
    clearTimeout(timeoutId);
  }
}

const http = {
  get: <T>(endpoint: string, schema?: z.ZodType<T>, options?: RequestOptions): Promise<T> =>
    request<T>(endpoint, schema, { ...options, method: 'GET' }),

  post: <T>(
    endpoint: string,
    schema?: z.ZodType<T>,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => request<T>(endpoint, schema, { ...options, method: 'POST', body }),

  put: <T>(
    endpoint: string,
    schema?: z.ZodType<T>,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => request<T>(endpoint, schema, { ...options, method: 'PUT', body }),

  patch: <T>(
    endpoint: string,
    schema?: z.ZodType<T>,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => request<T>(endpoint, schema, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, schema?: z.ZodType<T>, options?: RequestOptions): Promise<T> =>
    request<T>(endpoint, schema, { ...options, method: 'DELETE' }),
};

export default http;
export { HttpError };
